import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Menu, X, LogIn, LogOut, LayoutDashboard, FileText, ChevronDown, UserCircle, ExternalLink } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LOGO = "https://customer-assets.emergentagent.com/job_circuit-solutions-3/artifacts/65qv1eg7_logo%20equi-tec%20m_trasp2.png";
const OLD_SITE_URL = "https://sites.google.com/view/equi-tec-com/language-sprache-lingua";

export const Navbar = () => {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const links = [
    { to: "/catalog", label: t("nav.catalog") },
    { to: "/about", label: t("nav.about") },
    { to: "/contact", label: t("nav.contact") },
  ];

  const handleLogout = () => {
    logout();
    navigate("/");
    setOpen(false);
  };

  const linkClass = ({ isActive }) =>
    `text-sm font-medium uppercase tracking-wide transition-colors ${
      isActive ? "text-blue-700" : "text-slate-700 hover:text-blue-700"
    }`;

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white" data-testid="navbar">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img src={LOGO} alt="equi-tec" className="h-9 w-auto" />
          </Link>

          <nav className="hidden items-center gap-8 lg:flex" data-testid="desktop-nav">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} className={linkClass} data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`}>
                {l.label}
              </NavLink>
            ))}
            <a href={OLD_SITE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm font-medium uppercase tracking-wide text-slate-700 transition-colors hover:text-blue-700" data-testid="nav-old-site">
              {t("nav.oldSite")} <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </nav>

          <div className="hidden items-center gap-4 lg:flex">
            <LanguageSwitcher />
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1.5 rounded-none bg-slate-900 px-4 py-2 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-slate-700" data-testid="my-account-trigger">
                    <UserCircle className="h-4 w-4" /> {t("nav.myAccount")} <ChevronDown className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 rounded-none">
                  {user.role === "admin" ? (
                    <DropdownMenuItem asChild data-testid="account-admin">
                      <Link to="/admin" className="flex cursor-pointer items-center gap-2"><LayoutDashboard className="h-4 w-4" /> {t("nav.admin")}</Link>
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem asChild data-testid="account-documents">
                      <Link to="/portal" className="flex cursor-pointer items-center gap-2"><FileText className="h-4 w-4" /> {t("nav.documents")}</Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="flex cursor-pointer items-center gap-2 text-red-600" data-testid="account-logout">
                    <LogOut className="h-4 w-4" /> {t("nav.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" className="flex items-center gap-1 rounded-none bg-blue-700 px-4 py-2 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-blue-800" data-testid="nav-login">
                <LogIn className="h-4 w-4" /> {t("nav.login")}
              </Link>
            )}
          </div>

          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-slate-200 bg-white lg:hidden" data-testid="mobile-nav">
          <div className="space-y-1 px-4 py-4">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)} className="block py-2 text-sm font-medium uppercase tracking-wide text-slate-700" data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.slice(1)}`}>
                {l.label}
              </NavLink>
            ))}
            <a href={OLD_SITE_URL} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} className="flex items-center gap-1 py-2 text-sm font-medium uppercase tracking-wide text-slate-700" data-testid="mobile-nav-old-site">
              {t("nav.oldSite")} <ExternalLink className="h-3.5 w-3.5" />
            </a>
            {user && (
              <div className="border-t border-slate-200 pt-2">
                <p className="py-1 text-xs font-bold uppercase tracking-wider text-slate-400">{t("nav.myAccount")}</p>
                {user.role === "admin" && (
                  <Link to="/admin" onClick={() => setOpen(false)} className="block py-2 text-sm font-medium uppercase tracking-wide text-slate-700">{t("nav.admin")}</Link>
                )}
                {user.role === "customer" && (
                  <Link to="/portal" onClick={() => setOpen(false)} className="block py-2 text-sm font-medium uppercase tracking-wide text-slate-700">{t("nav.documents")}</Link>
                )}
              </div>
            )}
            <div className="py-3"><LanguageSwitcher /></div>
            {user ? (
              <button onClick={handleLogout} className="w-full rounded-none bg-slate-900 px-4 py-2 text-sm font-bold uppercase tracking-wide text-white">{t("nav.logout")}</button>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="block w-full rounded-none bg-blue-700 px-4 py-2 text-center text-sm font-bold uppercase tracking-wide text-white">{t("nav.login")}</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
